/**
 * System language selector.
 *
 * Lives beside the theme toggle at the bottom of the shell: both are
 * "how the app presents itself to me" controls, so they belong together.
 *
 * A native <select> is deliberate — it is the most accessible and the most
 * familiar control for a long list, it works on every device without a
 * popover library, and the keyboard and screen-reader behaviour comes for
 * free. We only restyle the chrome around it.
 *
 * Changing the language goes through the shared `setLanguage`, which swaps the
 * i18next language, persists it to `oc-language`, and keeps `<html lang>` in
 * step (so Greek uppercasing and hyphenation stay correct).
 */

import { useTranslation } from 'react-i18next';
import { LANGUAGES, LANGUAGE_NAMES, type Language } from '@opencomply/shared';
import { setLanguage } from '../i18n';
import { IconGlobe } from './icons';

function IconChevron(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden {...props}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function LanguageSwitcher({ className, iconOnly = false }: {
  className?: string;
  /** Mobile: a globe-sized trigger (the native picker still opens on tap). */
  iconOnly?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const current: Language = (LANGUAGES as readonly string[]).includes(i18n.language)
    ? (i18n.language as Language)
    : 'en';

  const options = LANGUAGES.map((l) => (
    <option key={l} value={l} className="bg-surface text-ink">
      {LANGUAGE_NAMES[l]}
    </option>
  ));

  if (iconOnly) {
    // The select fills the button and is transparent, so the whole globe is the
    // tap target and the OS language picker opens, with no wide label on a
    // cramped bar.
    return (
      <span className={`relative inline-flex p-2 rounded-lg text-ink-secondary hover:bg-surface-sunken ${className ?? ''}`}>
        <IconGlobe className="w-5 h-5 pointer-events-none" aria-hidden />
        <select
          value={current}
          onChange={(e) => setLanguage(e.target.value as Language)}
          aria-label={t('common.language')}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        >
          {options}
        </select>
      </span>
    );
  }

  return (
    <label
      className={`relative flex items-center h-9 w-full rounded-xl border border-line
        text-ink-secondary hover:bg-surface-sunken focus-within:ring-2 focus-within:ring-brand/40
        ${className ?? ''}`}
    >
      <IconGlobe className="w-4 h-4 shrink-0 absolute left-3 pointer-events-none" aria-hidden />
      <span className="sr-only">{t('common.language')}</span>
      <select
        value={current}
        onChange={(e) => setLanguage(e.target.value as Language)}
        aria-label={t('common.language')}
        className="appearance-none bg-transparent cursor-pointer text-sm text-ink
          h-full w-full rounded-xl focus:outline-none pl-9 pr-8"
      >
        {options}
      </select>
      <IconChevron className="w-4 h-4 shrink-0 absolute right-2.5 pointer-events-none opacity-70" />
    </label>
  );
}
